import {
  additionalRequirementCopy,
  dataRequirementCopy,
  spaceTimeRequirementCopy
} from '@shared/all/domains/factiiiRequirementOptions';
import { FactiiiRequirement } from '@shared/all/domains/factiiis';

import type { AddFactiiiState } from './formState';

/** Everything a tag can carry as its value, most-used first. The four source
 *  kinds sit in the same single-choice group — a tag holds one value — so they
 *  get a sub-heading rather than a control of their own. */
export const VALUE_ORDER: (FactiiiRequirement | null)[] = [
  null,
  FactiiiRequirement.DATA_REQUIRED,
  FactiiiRequirement.INTEGER_REQUIRED,
  FactiiiRequirement.DISAPPEARING_DATA
];

export const SOURCE_ORDER: FactiiiRequirement[] = [
  FactiiiRequirement.HUMAN_SOURCE,
  FactiiiRequirement.GOVERNMENT_SOURCE,
  FactiiiRequirement.ENTERPRISE_SOURCE,
  FactiiiRequirement.ANONYMOUS_SOURCE
];

/** Requirements about the post itself, which almost every factiii considers. */
export const POST_ORDER: FactiiiRequirement[] = [
  FactiiiRequirement.MEDIA_REQUIRED,
  FactiiiRequirement.VIDEO_REQUIRED,
  FactiiiRequirement.CONTENT_REQUIRED,
  FactiiiRequirement.CAMERA,
  FactiiiRequirement.SOURCE_REQUIRED,
  FactiiiRequirement.POST_REFERENCE_REQUIRED
];

/** The rare ones, kept off the common list so they don't set its tone. */
export const EXTRA_ORDER: FactiiiRequirement[] = [
  FactiiiRequirement.WIKI,
  FactiiiRequirement.NO_POST_DUPLICATES,
  FactiiiRequirement.POSTFACTIII_MODERATION_REQUIRED,
  FactiiiRequirement.MEDIA_MATCH,
  FactiiiRequirement.ROOT
];

export const additionalOptionFor = (requirement: FactiiiRequirement) =>
  additionalRequirementCopy.find((option) => option.value === requirement);

export const valueOptionFor = (requirement: FactiiiRequirement | null) =>
  dataRequirementCopy.find((option) => option.value === requirement);

const chosenLabels = (state: AddFactiiiState, order: FactiiiRequirement[]) =>
  order
    .filter((requirement) => state.extraRequirements.includes(requirement))
    .map(
      (requirement) => additionalOptionFor(requirement)?.label ?? requirement
    );

export type SectionKey =
  | 'space'
  | 'value'
  | 'place'
  | 'post'
  | 'extras'
  | 'group'
  | 'bot'
  | 'rules';

export type SectionIconName =
  | 'Bot'
  | 'Building2'
  | 'Hash'
  | 'Layers'
  | 'ListChecks'
  | 'MapPin'
  | 'ScrollText'
  | 'Settings2';

type OptionSectionMeta = {
  key: SectionKey;
  title: string;
  icon: SectionIconName;
  /** What the row shows on the right — the list has to be readable unopened. */
  summary: (state: AddFactiiiState) => string;
  /** True when the setting is switched on but not finished, so the row can say
   *  so in the accent colour instead of reading as done. */
  attention?: (state: AddFactiiiState) => boolean;
};

/**
 * Every optional setting, in one registry. Both platforms render their option
 * lists and highlighted rows from it and draw their own bodies per key, so a
 * summary can never drift from the control it describes.
 */
export const OPTION_SECTIONS: OptionSectionMeta[] = [
  {
    key: 'space',
    title: 'Space',
    icon: 'Building2',
    summary: (state) => (state.spaceSlug ? `s/${state.spaceSlug}` : 'None')
  },
  {
    key: 'value',
    title: 'What each tag carries',
    icon: 'Hash',
    summary: (state) =>
      valueOptionFor(state.dataRequirement)?.label ?? 'Nothing extra'
  },
  {
    key: 'place',
    title: 'Place and time',
    icon: 'MapPin',
    summary: (state) => {
      const parts = [
        state.locationRequirement
          ? spaceTimeRequirementCopy.find(
              (option) => option.value === state.locationRequirement
            )?.label
          : null,
        state.needsTime ? 'Pick a Time' : null
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : 'Neither';
    }
  },
  {
    key: 'post',
    title: 'What the post must include',
    icon: 'ListChecks',
    summary: (state) => chosenLabels(state, POST_ORDER).join(' · ') || 'Nothing'
  },
  {
    key: 'extras',
    title: 'Extras',
    icon: 'Settings2',
    summary: (state) => chosenLabels(state, EXTRA_ORDER).join(' · ') || 'None'
  },
  {
    key: 'group',
    title: 'Grouping',
    icon: 'Layers',
    summary: (state) =>
      state.createAsGroup
        ? state.groupFactiiiIds.length
          ? `With ${state.groupFactiiiIds.length}`
          : 'Pick factiiis'
        : 'Off',
    attention: (state) =>
      state.createAsGroup && state.groupFactiiiIds.length === 0
  },
  {
    key: 'bot',
    title: 'AI replies',
    icon: 'Bot',
    summary: (state) =>
      state.botEnabled ? (state.modelId ? 'On' : 'Choose a model') : 'Off',
    attention: (state) => state.botEnabled && !state.modelId
  },
  {
    key: 'rules',
    title: 'Moderator rules',
    icon: 'ScrollText',
    summary: (state) => (state.rules.length ? `${state.rules.length}` : 'None')
  }
];

export const findSection = (key: SectionKey) =>
  OPTION_SECTIONS.find((section) => section.key === key)!;

/** A bot can only auto-tag factiiis whose requirements it can actually satisfy
 *  from the text it generates — see `generateFactiiiBotReply`. */
const ALLOWED_TAGGABLE: Record<string, string> = {
  [FactiiiRequirement.CONTENT_REQUIRED]: 'Write Something',
  [FactiiiRequirement.LOCATION_REQUIRED]: 'Add Location',
  [FactiiiRequirement.INTEGER_REQUIRED]: 'Add a Number',
  [FactiiiRequirement.DATA_REQUIRED]: 'Extra Details',
  [FactiiiRequirement.MEDIA_REQUIRED]: 'Add Photo or Video'
};

export const taggableError = (requirements: string[] | null | undefined) => {
  if (!requirements?.length) return null;
  const unsupported = requirements.some(
    (requirement) => !ALLOWED_TAGGABLE[requirement]
  );
  return unsupported
    ? `A bot can only tag factiiis needing: ${Object.values(ALLOWED_TAGGABLE).join(', ')}.`
    : null;
};
